import React from 'react';

import './FilterPanel.css';
import {
  DIVERSITY_QUALIFICATION_FILTER_TREE_NODE_ID,
  FILTER_TREE_TYPE,
  FilterTreeType,
  INVITED_EVENT_COUNT_MAX,
  SEARCH_FILTER_NAME
} from 'constants/filter';
import { LIST_CATEGORY_ID } from 'constants/list';
import { SEARCH_MODE, SearchModeType } from 'constants/search';
import {
  AppliedFilterChipType,
  searchResultActiveUserFilter,
  searchResultNameFilter,
  searchResultPowerScoreFilter
} from 'util/filter';
import { useSearchContext } from 'hooks/search/useSearchContext';
import ActivityIndicator from 'components/activity/ActivityIndicator';
import AppliedFilterChipPanel from 'components/panel/filter/AppliedFilterChipPanel';
import CountryStateFilterSearchPanel from 'components/panel/filter/CountryStateFilterSearchPanel';
import FilterRequestPanel from 'components/panel/filter/FilterRequestPanel';
import FilterSearchPanel from 'components/panel/filter/FilterSearchPanel';
import GlobalFilterSearchPanel, { GlobalFilterSearchPanelProps } from 'components/panel/filter/GlobalFilterSearchPanel';
import ProductServiceFilterSearchPanel from 'components/panel/filter/ProductServiceFilterSearchPanel';
import SearchTextField from 'components/input/SearchTextField';
import SourceFilterSearchPanel from 'components/panel/filter/SourceFilterSearchPanel';
import SupplierActivityFilterSearchPanel from 'components/panel/filter/SupplierActivityFilterSearchPanel';
import CustomSelect from 'components/customDropdown/CustomSelect';

//
// --- Types ---

interface FilterPanelProps {
  /** intended to be logged in person company key, needed for powerscore filters when searches re-run */
  buyerCompanyKey: number;
  /** intended to be logged in person company name, needed for source filter tooltip */
  buyerCompanyName: string;
  hideContactNameFilter?: boolean;
  hideGlobalFilterSearchPanel?: boolean;
  hideSupplierRequestPanel?: boolean;
  useNewSupplierSearch: boolean;
  isDiscovery: boolean;
}

//
// --- FilterPanel Component ---

const FilterPanel: React.FC<FilterPanelProps> = props => {
  const {
    buyerCompanyKey,
    buyerCompanyName,
    hideContactNameFilter = false,
    hideGlobalFilterSearchPanel = false,
    hideSupplierRequestPanel = false,
    useNewSupplierSearch = false,
    isDiscovery
  } = props;

  const [
    {
      appliedFilterChips,
      checkedFilterIdsMap,
      filterTreeDataMap,
      invitedEventCountRangeEnd,
      invitedEventCountRangeStart,
      invitedPortalCountRangeEnd,
      invitedPortalCountRangeStart,
      isFilterTreeDataLoading,
      openFilterIdsMap,
      searchFilters,
      searchMode,
      selectedListCategoryId,
      searchPhraseForLastSearch
    },
    {
      addCheckedFilter,
      addSearchFilter,
      removeCheckedFilter,
      removeSearchFilter,
      updateFilterOpenState,
      updateInvitedEventCountFilter,
      updateInvitedPortalCountFilter,
      updateSearchMode
    }
  ] = useSearchContext();

  /**
   * Adds the "Approved" diversity qualification filter whenever a diversity classification filter is added
   * The ref to keep track of previous length of classification filters is necessary to detect when the change
   * to diversity classification filters was an addition.
   * addCheckedFilter is excluded from the dependencies because including it causes an infinite loop.
   */
  const prevCheckedClassificationFiltersLengthRef = React.useRef(0);
  React.useEffect(() => {
    if (
      checkedFilterIdsMap[FILTER_TREE_TYPE.DIVERSITY_CLASSIFICATION].length >
      prevCheckedClassificationFiltersLengthRef.current
    ) {
      if (
        !checkedFilterIdsMap[FILTER_TREE_TYPE.QUALIFICATION].includes(
          DIVERSITY_QUALIFICATION_FILTER_TREE_NODE_ID.APPROVED
        )
      ) {
        addCheckedFilter(
          buyerCompanyKey,
          FILTER_TREE_TYPE.QUALIFICATION,
          DIVERSITY_QUALIFICATION_FILTER_TREE_NODE_ID.APPROVED,
          useNewSupplierSearch
        );
      }
    }
    prevCheckedClassificationFiltersLengthRef.current =
      checkedFilterIdsMap[FILTER_TREE_TYPE.DIVERSITY_CLASSIFICATION].length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyerCompanyKey, checkedFilterIdsMap]);

  const handleAppliedFilterChipDelete = (appliedFilterChip: AppliedFilterChipType) => {
    if (appliedFilterChip.filterTreeType === FILTER_TREE_TYPE.INVITED_EVENT_COUNT) {
      updateInvitedEventCountFilter(buyerCompanyKey, 0, INVITED_EVENT_COUNT_MAX, useNewSupplierSearch);
    } else if (appliedFilterChip.filterTreeType === FILTER_TREE_TYPE.INVITED_PORTAL_COUNT) {
      updateInvitedPortalCountFilter(buyerCompanyKey, 0, INVITED_EVENT_COUNT_MAX, useNewSupplierSearch);
    } else if (appliedFilterChip.filterTreeType) {
      removeCheckedFilter(
        buyerCompanyKey,
        appliedFilterChip.filterTreeType,
        appliedFilterChip.id,
        useNewSupplierSearch
      );
    }
  };

  const handleGenericFilterCheckedChange =
    (filterTreeType: FilterTreeType) => (filterId: string, isChecked: boolean) => {
      if (isChecked) {
        addCheckedFilter(buyerCompanyKey, filterTreeType, filterId, useNewSupplierSearch);
      } else {
        removeCheckedFilter(buyerCompanyKey, filterTreeType, filterId, useNewSupplierSearch);
      }
    };

  const handleGenericFilterOpenChange = (filterTreeType: FilterTreeType) => (filterId: string, isOpen: boolean) => {
    updateFilterOpenState(filterTreeType, filterId, isOpen);
  };

  const handleItemFilterCheckedChange = (itemFilterId: string, isChecked: boolean) => {
    if (isChecked) {
      if (!checkedFilterIdsMap[FILTER_TREE_TYPE.ITEM].includes(itemFilterId)) {
        addCheckedFilter(buyerCompanyKey, FILTER_TREE_TYPE.ITEM, itemFilterId, useNewSupplierSearch);
      }
    } else {
      removeCheckedFilter(buyerCompanyKey, FILTER_TREE_TYPE.ITEM, itemFilterId, useNewSupplierSearch);
    }
  };

  const handleItemFilterCleared = (itemFilterId: string) => {
    removeCheckedFilter(buyerCompanyKey, FILTER_TREE_TYPE.ITEM, itemFilterId, useNewSupplierSearch, {
      removeAll: true
    });
  };

  const handleGlobalFilterCheckedChange: GlobalFilterSearchPanelProps['onFilterCheckedChange'] = (
    treeNodeId,
    isChecked
  ) => {
    if (treeNodeId === SEARCH_FILTER_NAME.ACTIVE_USER) {
      if (isChecked) {
        addSearchFilter(searchResultActiveUserFilter);
      } else {
        removeSearchFilter(SEARCH_FILTER_NAME.ACTIVE_USER);
      }
    }

    if (treeNodeId === SEARCH_FILTER_NAME.POWERSCORE) {
      if (isChecked) {
        addSearchFilter({ ...searchResultPowerScoreFilter, filterData: 1 });
      } else {
        removeSearchFilter(SEARCH_FILTER_NAME.POWERSCORE);
      }
    }
  };

  const handleNameFilterTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newNameFilterValue = event.target.value;

    if (newNameFilterValue.length) {
      addSearchFilter({ ...searchResultNameFilter, filterData: newNameFilterValue });
    } else {
      removeSearchFilter(SEARCH_FILTER_NAME.PERSON_NAME);
    }
  };

  const handleSupplierActivityEventCountFilterRangeChange = (rangeStart: number, rangeEnd: number) => {
    updateInvitedEventCountFilter(buyerCompanyKey, rangeStart, rangeEnd, useNewSupplierSearch);
  };

  const handleSupplierActivityPortalCountFilterRangeChange = (rangeStart: number, rangeEnd: number) => {
    updateInvitedPortalCountFilter(buyerCompanyKey, rangeStart, rangeEnd, useNewSupplierSearch);
  };

  const nameFilter = searchFilters.find(searchFilter => searchFilter.name === SEARCH_FILTER_NAME.PERSON_NAME);

  const handleIsDiscoverySearch = (searchMode: SearchModeType) => {
    updateSearchMode(searchMode, searchPhraseForLastSearch);
  };

  const isIPAFBSeach = searchMode === SEARCH_MODE.IPAFB_SUPPLIER_SEARCH;

  const hideGlobalFilterSearchPanelVar = hideGlobalFilterSearchPanel || isIPAFBSeach;
  const hideContactNameFilterVar = hideContactNameFilter || isIPAFBSeach;

  return (
    <div className="filter-panel" data-testid="filter-panel">
      <div className="filter-panel-content">
        <CustomSelect
          className="search-mode-panel-mode-dropdown"
          dataTestId="search-mode-panel-mode-dropdown"
          onChange={handleIsDiscoverySearch}
          value={searchMode}
          displayEmpty
          items={[
            {
              key: SEARCH_MODE.SUPPLIER_SEARCH,
              label: 'WM Registered Suppliers',
              toolTipVal:
                'Search suppliers who are registered on the WM Supply Chain Intelligence Platform. This pool includes suppliers that actively maintain profiles and engage through the platform.'
            },
            {
              key: SEARCH_MODE.IPAFB_SUPPLIER_SEARCH,
              label: 'Supplier Discovery',
              toolTipVal:
                'Search suppliers sourced from WM’s Factbase of Spend data. This pool helps you discover additional suppliers beyond those registered on the platform.'
            }
          ]}
          hiddenItems={!useNewSupplierSearch ? [SEARCH_MODE.IPAFB_SUPPLIER_SEARCH] : []}
          render={isDiscovery}
        />
        {!hideSupplierRequestPanel && (
          <>
            <FilterRequestPanel />
            <div className="filter-panel-divider"></div>
          </>
        )}
        {!hideContactNameFilterVar && (
          <>
            <SearchTextField
              className="filter-panel-name-search-textfield"
              helperText="Filter results by contact name"
              inputProps={{
                'aria-label': 'Find Contacts'
              }}
              onChange={handleNameFilterTextFieldChange}
              placeholder="Contact name"
              value={nameFilter && nameFilter.filterData ? nameFilter.filterData : ''}
              fullWidth
              useNewSupplierSearch={isIPAFBSeach}
            />
            <div className="filter-panel-divider"></div>
          </>
        )}
        <AppliedFilterChipPanel
          appliedFilterChips={appliedFilterChips}
          onAppliedFilterChipDelete={handleAppliedFilterChipDelete}
        />
        {!hideGlobalFilterSearchPanelVar && (
          <GlobalFilterSearchPanel
            activeSearchFilterNames={searchFilters.map(filter => filter.name as string)}
            disabledSearchFilterNames={
              searchMode === SEARCH_MODE.COMPANY_LISTS &&
              selectedListCategoryId === LIST_CATEGORY_ID.POWER_SCORE_CONTACTS
                ? [SEARCH_FILTER_NAME.POWERSCORE]
                : []
            }
            onFilterCheckedChange={handleGlobalFilterCheckedChange}
            render
          />
        )}
        {isFilterTreeDataLoading ? (
          <ActivityIndicator size={25} hideMessage />
        ) : (
          <>
            <ProductServiceFilterSearchPanel
              filterTreeData={
                Array.isArray(filterTreeDataMap[FILTER_TREE_TYPE.ITEM]) &&
                filterTreeDataMap[FILTER_TREE_TYPE.ITEM].length > 0
                  ? [
                      {
                        ...filterTreeDataMap[FILTER_TREE_TYPE.ITEM][0],
                        canClear:
                          Array.isArray(checkedFilterIdsMap[FILTER_TREE_TYPE.ITEM]) &&
                          checkedFilterIdsMap[FILTER_TREE_TYPE.ITEM].length > 0
                      }
                    ]
                  : []
              }
              onFilterCheckedChange={handleItemFilterCheckedChange}
              onFilterCleared={handleItemFilterCleared}
              onFilterOpenStateChange={handleGenericFilterOpenChange(FILTER_TREE_TYPE.ITEM)}
              openFilterIds={openFilterIdsMap[FILTER_TREE_TYPE.ITEM]}
              render
            />
            <CountryStateFilterSearchPanel
              filterTreeData={filterTreeDataMap[FILTER_TREE_TYPE.COUNTRY_OR_STATE]}
              onFilterCheckedChange={handleGenericFilterCheckedChange(FILTER_TREE_TYPE.COUNTRY_OR_STATE)}
              onFilterOpenStateChange={handleGenericFilterOpenChange(FILTER_TREE_TYPE.COUNTRY_OR_STATE)}
              openFilterIds={openFilterIdsMap[FILTER_TREE_TYPE.COUNTRY_OR_STATE]}
              render
            />
            <FilterSearchPanel
              className="filter-search-panel-diversity-tree"
              filterTreeData={filterTreeDataMap[FILTER_TREE_TYPE.DIVERSE]}
              onFilterCheckedChange={handleGenericFilterCheckedChange(FILTER_TREE_TYPE.DIVERSE)}
              onFilterOpenStateChange={handleGenericFilterOpenChange(FILTER_TREE_TYPE.DIVERSE)}
              openFilterIds={openFilterIdsMap[FILTER_TREE_TYPE.DIVERSE]}
              render
            />
            <FilterSearchPanel
              className="filter-search-panel-diversity-classification"
              filterTreeData={filterTreeDataMap[FILTER_TREE_TYPE.DIVERSITY_CLASSIFICATION]}
              onFilterCheckedChange={handleGenericFilterCheckedChange(FILTER_TREE_TYPE.DIVERSITY_CLASSIFICATION)}
              onFilterOpenStateChange={handleGenericFilterOpenChange(FILTER_TREE_TYPE.DIVERSITY_CLASSIFICATION)}
              openFilterIds={openFilterIdsMap[FILTER_TREE_TYPE.DIVERSITY_CLASSIFICATION]}
              render={!isIPAFBSeach}
            />
            <FilterSearchPanel
              className="filter-search-panel-qualification-tree"
              filterTreeData={filterTreeDataMap[FILTER_TREE_TYPE.QUALIFICATION]}
              onFilterCheckedChange={handleGenericFilterCheckedChange(FILTER_TREE_TYPE.QUALIFICATION)}
              onFilterOpenStateChange={handleGenericFilterOpenChange(FILTER_TREE_TYPE.QUALIFICATION)}
              openFilterIds={openFilterIdsMap[FILTER_TREE_TYPE.QUALIFICATION]}
              render={!isIPAFBSeach}
            />
            <FilterSearchPanel
              className="filter-search-panel-segmentation"
              filterTreeData={filterTreeDataMap[FILTER_TREE_TYPE.SEGMENTATION]}
              onFilterCheckedChange={handleGenericFilterCheckedChange(FILTER_TREE_TYPE.SEGMENTATION)}
              onFilterOpenStateChange={handleGenericFilterOpenChange(FILTER_TREE_TYPE.SEGMENTATION)}
              openFilterIds={openFilterIdsMap[FILTER_TREE_TYPE.SEGMENTATION]}
              render={!isIPAFBSeach}
            />
          </>
        )}
        <SourceFilterSearchPanel
          companyName={buyerCompanyName}
          filterTreeData={filterTreeDataMap[FILTER_TREE_TYPE.SOURCE]}
          onFilterCheckedChange={handleGenericFilterCheckedChange(FILTER_TREE_TYPE.SOURCE)}
          onFilterOpenStateChange={handleGenericFilterOpenChange(FILTER_TREE_TYPE.SOURCE)}
          openFilterIds={openFilterIdsMap[FILTER_TREE_TYPE.SOURCE]}
          render={!isIPAFBSeach}
        />
        <SupplierActivityFilterSearchPanel
          className="filter-panel-supplier-activity"
          invitedEventCountRangeEnd={invitedEventCountRangeEnd}
          invitedEventCountRangeStart={invitedEventCountRangeStart}
          invitedPortalCountRangeEnd={invitedPortalCountRangeEnd}
          invitedPortalCountRangeStart={invitedPortalCountRangeStart}
          onInvitedEventRangeChange={handleSupplierActivityEventCountFilterRangeChange}
          onInvitedPortalRangeChange={handleSupplierActivityPortalCountFilterRangeChange}
          render={!isIPAFBSeach}
        />
      </div>
    </div>
  );
};

export default FilterPanel;
